import * as ko from 'knockout'
import { Background } from './pages/background'
import { Page } from './pages/page'
import { CollectorLink, EventItem, Location, Organization } from '@tixa/schema'
import { pageState } from '~/store/page'
import { Tracker } from '~/utils/analytics'
import { Translations } from '~/utils/language'

import './components/item-event/item-event'
import './components/page-sidebar/page-sidebar'
import './components/search-empty-state/search-empty-state'

import './components/item-event/item-event.less'
import './search-results.less'

export type SearchTabId = 'collector' | 'organizer' | 'location' | 'event'

export interface SearchTab {
  id: SearchTabId
  i18n: string
}

const LOAD_MORE_STEP = 6
const LOAD_MORE_DELAY_MS = 500

/** Estimate how many cards fit in the viewport (3 columns, ~300px per row) */
function getInitialVisibleCount(): number {
  if (typeof window === 'undefined') return LOAD_MORE_STEP
  const availableHeight = window.innerHeight - 380
  const rows = Math.max(1, Math.ceil(availableHeight / 300))
  return Math.max(LOAD_MORE_STEP, rows * 3)
}

export class SearchResultsPage extends Page {
  public events = ko.observableArray<EventItem>([])
  public locations = ko.observableArray<Location>([])
  public organizers = ko.observableArray<Organization>([])
  public collectors = ko.observableArray<CollectorLink>([])
  public searchTerm = ko.observable('')
  public searchRunning = ko.observable(false)
  public hasSearched = ko.observable(false)
  public selectedTab = ko.observable<SearchTabId>('event')

  public visibleEventCount = ko.observable(LOAD_MORE_STEP)
  public loadingMoreEvents = ko.observable(false)

  public visibleEvents = ko.computed(() =>
    this.events().slice(0, this.visibleEventCount())
  )

  public hasMoreEvents = ko.computed(
    () => this.visibleEventCount() < this.events().length
  )

  public loadMoreEventsLabel = ko.computed(() => {
    const total = this.events().length
    const visible = this.visibleEventCount()
    const remaining = total - visible
    if (remaining <= 0) return ''
    const t = Translations as Record<string, string>
    if (remaining <= LOAD_MORE_STEP) {
      return `${t.searchLoadRemaining ?? 'Load remaining'} (${remaining})`
    }
    return t.searchLoadMore ?? 'Load 6 more'
  })

  public tabs: SearchTab[] = [
    { id: 'collector', i18n: 'searchTabCollectorLinks' },
    { id: 'event', i18n: 'searchTabEvent' },
    { id: 'location', i18n: 'searchTabLocation' },
    { id: 'organizer', i18n: 'searchTabOrganizer' },
  ]

  public visibleTabs = ko.computed(() =>
    this.tabs.filter((tab) => this.getCountForTab(tab.id) > 0)
  )

  public searchQueryLabel = ko.computed(() => {
    const term = this.searchTerm()
    return term ? `"${term}"` : ''
  })

  public hasSearchTerm = ko.computed(() => this.searchTerm().length > 0)

  public showResults = ko.computed(
    () => this.hasSearched() && !this.searchRunning()
  )
  public showSearchPrompt = ko.computed(
    () => !this.hasSearched() && !this.searchRunning()
  )
  public showCollectorTab = ko.computed(
    () => this.showResults() && this.selectedTab() === 'collector'
  )
  public showOrganizerTab = ko.computed(
    () => this.showResults() && this.selectedTab() === 'organizer'
  )
  public showLocationTab = ko.computed(
    () => this.showResults() && this.selectedTab() === 'location'
  )
  public showEventTab = ko.computed(
    () => this.showResults() && this.selectedTab() === 'event'
  )
  public hasEvents = ko.computed(() => this.events().length > 0)
  public hasLocations = ko.computed(() => this.locations().length > 0)
  public hasOrganizers = ko.computed(() => this.organizers().length > 0)
  public hasCollectors = ko.computed(() => this.collectors().length > 0)
  public showNoResults = ko.computed(
    () =>
      (this.showCollectorTab() && !this.hasCollectors()) ||
      (this.showOrganizerTab() && !this.hasOrganizers()) ||
      (this.showLocationTab() && !this.hasLocations()) ||
      (this.showEventTab() && !this.hasEvents())
  )

  public isTabSelected = (tabId: SearchTabId): boolean =>
    this.selectedTab() === tabId

  public selectTab = (tabId: SearchTabId): void => {
    this.selectedTab(tabId)
  }

  public fireSelectItem = (event: EventItem, index: number): void => {
    if (Tracker.selectSearchResultItem) {
      Tracker.selectSearchResultItem(this.searchTerm(), event, index)
    }
  }

  public getEventItemParams = (
    event: EventItem,
    index0Based: number
  ): EventItem & { onSelect: () => void } => ({
    ...event,
    onSelect: () => this.fireSelectItem(event, index0Based + 1),
  })

  public getCountForTab(tabId: SearchTabId): number {
    switch (tabId) {
      case 'collector':
        return this.collectors().length
      case 'event':
        return this.events().length
      case 'organizer':
        return this.organizers().length
      case 'location':
        return this.locations().length
      default:
        return 0
    }
  }

  public getTabLabelWithCount(tab: SearchTab): string {
    const count = this.getCountForTab(tab.id)
    const label = (Translations as Record<string, string>)[tab.i18n] ?? tab.i18n
    return `${label} (${count})`
  }

  public loadMoreEvents = (): void => {
    if (this.loadingMoreEvents()) return
    const total = this.events().length
    const visible = this.visibleEventCount()
    const remaining = total - visible
    if (remaining <= 0) return
    this.loadingMoreEvents(true)
    setTimeout(() => {
      if (remaining <= LOAD_MORE_STEP) {
        this.visibleEventCount(total)
      } else {
        this.visibleEventCount(visible + LOAD_MORE_STEP)
      }
      this.loadingMoreEvents(false)
    }, LOAD_MORE_DELAY_MS)
  }

  constructor() {
    super()
    Background.getInstance().setDefaultBackground()
    this.visibleTabs.subscribe((visible) => {
      if (visible.length > 0 && this.getCountForTab(this.selectedTab()) === 0) {
        this.selectedTab(visible[0].id)
      }
    })
    this.runSearchFromUrl()
  }

  private async runSearchFromUrl(): Promise<void> {
    const params = new URLSearchParams(window.location.search)
    const q = params.get('q')?.trim()
    const tabParam = params.get('tab')
    if (!q) {
      return
    }
    this.searchTerm(q)
    this.searchRunning(true)
    this.hasSearched(true)
    const controller = new AbortController()
    try {
      const result = await pageState.api.search.search(q, controller)
      const rawEvents = result.events?.items ?? []
      const rawLocations = result.locations?.items ?? []
      const rawOrganizers = result.organizers?.items ?? []
      const rawCollectors = result.collectors?.items ?? []
      this.events(rawEvents)
      this.locations(rawLocations)
      this.organizers(rawOrganizers)
      this.collectors(rawCollectors)
      const initial = getInitialVisibleCount()
      this.visibleEventCount(Math.min(initial, rawEvents.length))
    } catch {
      this.events([])
      this.locations([])
      this.organizers([])
      this.collectors([])
      this.visibleEventCount(LOAD_MORE_STEP)
    } finally {
      this.searchRunning(false)
      if (Tracker.viewSearchResultList) {
        Tracker.viewSearchResultList(q, this.events())
      }
      const validTabs: SearchTabId[] = [
        'event',
        'location',
        'organizer',
        'collector',
      ]
      if (tabParam && validTabs.includes(tabParam as SearchTabId)) {
        this.selectedTab(tabParam as SearchTabId)
      }
    }
  }
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const page = new SearchResultsPage()
